import { useToast } from '../composables/useToast'
import { isRequestAborted } from './isRequestAborted'
import {
  isRegisteredUserRequiredError,
  REGISTERED_ACCESS_ONLY_TOAST,
} from './isRegisteredUserRequiredError'
import { isExpectedSessionEndError, isRefreshFailureHandled } from './impersonationSession'

function requestUrlForErrorMessage(url: unknown): string | undefined {
  if (typeof url !== 'string' || !url.length) return undefined
  return url.split('?')[0]
}

function extractApiEnvelope(response: any) {
  const data = response?.data
  if (!data || typeof data !== 'object') {
    return { message: undefined, code: undefined }
  }

  const nested =
    data.data && typeof data.data === 'object' ? (data.data as Record<string, unknown>) : undefined

  const message =
    typeof data.message === 'string' && data.message.length > 0
      ? data.message
      : typeof nested?.message === 'string' && nested.message.length > 0
        ? nested.message
        : undefined

  const code = data.code ?? nested?.code

  return { message, code }
}

function buildUnsuccessfulResponseMessage(response: any): string {
  const { message, code } = extractApiEnvelope(response)
  const httpStatus = response?.status
  const url = requestUrlForErrorMessage(response?.config?.url)

  const parts: string[] = []
  if (message) parts.push(message)
  if (code !== undefined && code !== null) {
    parts.push(`code: ${code}`)
  } else if (httpStatus !== undefined) {
    parts.push(`HTTP status: ${httpStatus}`)
  }
  if (url) parts.push(`url: ${url}`)

  return parts.length > 0 ? parts.join(' | ') : 'Unsuccessful Escribonauta API response'
}

function handleEscribonautaResponse(response: any, _?: string) {
  if (response?.data?.code >= 200 && response?.data?.code <= 300) {
    return response.data
  }

  const error = new Error(buildUnsuccessfulResponseMessage(response)) as Error & {
    response?: typeof response
    code?: unknown
  }
  error.response = response
  const { code } = extractApiEnvelope(response)
  if (code !== undefined && code !== null) {
    error.code = code
  }
  throw error
}

function handleEscribonautaError(error: any, customErrorMessage?: string) {
  if (isRequestAborted(error)) {
    return Promise.reject(error)
  }

  if (isExpectedSessionEndError(error)) {
    return Promise.reject(error)
  }

  if (isRefreshFailureHandled(error)) {
    return Promise.reject(error)
  }

  const { triggerErrorToast } = useToast()

  if (isRegisteredUserRequiredError(error)) {
    triggerErrorToast(REGISTERED_ACCESS_ONLY_TOAST)
    return Promise.reject(error)
  }

  if (customErrorMessage) {
    triggerErrorToast(customErrorMessage)
  }
  return Promise.reject(error)
}

export default {
  handleEscribonautaResponse,
  handleEscribonautaError,
}
